import {Component, ElementRef, OnInit, ViewChild, Output, EventEmitter, Input, OnChanges} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {CampaignsService} from 'src/app/core/services/api/campaigns.service';

import {MatChipInputEvent} from '@angular/material/chips';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {BehaviorSubject, Observable, combineLatest} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { Campaign } from 'src/app/core/contracts/models/campaign.interface';
import { Swal2 } from 'src/app/core/utils/swal2.util';

/** Opción del desplegable: etiqueta existente, existente parecida, crear nueva o aviso */
interface LabelOption {
    kind: 'existing' | 'similar' | 'create' | 'info';
    slug: string;
    label?: any;
    /** Para 'create': etiquetas existentes parecidas a la que se quiere crear */
    similar?: any[];
}

@Component({
    selector: 'app-campaign-labels',
    templateUrl: './campaign-labels.component.html',
    styleUrls: ['./campaign-labels.component.scss']
})
export class CampaignLabelsComponent implements OnInit, OnChanges {

    visible = true;
    removable = true;
    addOnBlur = false;
    readonly separatorKeysCodes: number[] = [ENTER, COMMA];
    etiquetas = [];

    filteredLabels: Observable<LabelOption[]>;
    allLabels: any[] = [];
    private allLabels$ = new BehaviorSubject<any[]>([]);

    @ViewChild('labelInput') labelInput: ElementRef<HTMLInputElement>;
    @ViewChild('autoLabel') matAutocomplete: MatAutocomplete;
    @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;

    labelForm = new UntypedFormControl('')

    @Input() campaign: Campaign;
    @Input() words: boolean;
    @Input() label: string;
    @Input() apparience: MatFormFieldAppearance = 'fill';
    /** Permite crear etiquetas nuevas desde el campo (se guardan al guardar la campaña) */
    @Input() allowCreate = false;
    @Output() outLabel = new EventEmitter<any>();

    constructor(
        private campaignsService: CampaignsService,
        private swal: Swal2
    ) {
    }

    ngOnInit(): void {
        this.campaignsService.getLabels().subscribe(response => {
            this.allLabels = response['etiquetas'] ?? [];
            this.allLabels$.next(this.allLabels);
        });

        this.filteredLabels = combineLatest([
            this.allLabels$,
            this.labelForm.valueChanges.pipe(startWith(''))
        ]).pipe(
            map(([, value]) => this.buildOptions(typeof value === 'string' ? value : ''))
        );
    }

    ngOnChanges() {
        if (this.campaign) {
            this.etiquetas = this.campaign.etiquetas ? this.campaign.etiquetas : [];
        }
    }

    /** Abre la lista al hacer clic en el campo, aunque ya tenga el foco */
    openList(): void {
        if (!this.autocompleteTrigger?.panelOpen) {
            this.autocompleteTrigger?.openPanel();
        }
    }

    /** Enter o coma con la lista cerrada: usar la existente o crearla */
    add(event: MatChipInputEvent): void {
        if (this.autocompleteTrigger?.panelOpen) {
            // La opción resaltada de la lista la maneja selected()
            return;
        }
        const text = this.clean(event.value ?? '');
        if (!text) {
            return;
        }
        const option = this.buildOptions(text).find(o => o.kind !== 'info');
        if (option) {
            this.choose(option);
        }
    }

    remove(label): void {
        const index = this.etiquetas.findIndex(et => et.slug == label);
        if (index >= 0) {
            this.etiquetas.splice(index, 1);
        }
        this.emit();
        this.labelForm.setValue(this.labelForm.value ?? '');
    }

    selected(event: MatAutocompleteSelectedEvent): void {
        this.choose(event.option.value as LabelOption);
    }

    private async choose(option: LabelOption): Promise<void> {
        if (option.kind === 'existing' || option.kind === 'similar') {
            this.pushLabel(option.label);
        } else if (option.kind === 'create') {
            await this.createLabel(option);
        }
        this.resetInput();
    }

    /** Crea una etiqueta nueva, advirtiendo si hay existentes parecidas */
    private async createLabel(option: LabelOption): Promise<void> {
        const similar = option.similar ?? [];
        if (similar.length) {
            const list = similar.slice(0, 5).map(l => `<li>${this.escape(l.slug)}</li>`).join('');
            const closest = similar[0];
            const answer = await this.swal.confirmWithAlternative({
                title: 'Hay etiquetas parecidas',
                html: `Vas a crear <b>«${this.escape(option.slug)}»</b>, pero ya existen:`
                    + `<ul style="text-align:left; margin:12px auto 0; display:inline-block">${list}</ul>`,
                confirmText: `Crear «${option.slug}»`,
                denyText: `Usar «${closest.slug}»`,
                cancelText: 'Volver',
            });
            if (answer === 'cancel') {
                return;
            }
            if (answer === 'deny') {
                this.pushLabel(closest);
                return;
            }
        }
        // Se guarda en la base al guardar la campaña (el backend la crea con firstOrCreate)
        this.pushLabel({ slug: option.slug, nueva: true });
    }

    private pushLabel(label: any): void {
        const exists = this.etiquetas.some(et => this.normalize(et.slug) === this.normalize(label.slug));
        if (!exists) {
            this.etiquetas.push(label);
            this.emit();
        }
    }

    private resetInput(): void {
        if (this.labelInput) {
            this.labelInput.nativeElement.value = '';
        }
        this.labelForm.setValue('');
        // Mantener la lista abierta para agregar varias seguidas
        setTimeout(() => this.openList());
    }

    private emit(): void {
        if (this.words) {
            this.outLabel.emit(this.etiquetas.map(el => el.slug));
        } else {
            this.outLabel.emit(this.etiquetas);
        }
    }

    /** Opciones del desplegable para el texto escrito */
    private buildOptions(text: string): LabelOption[] {
        const query = this.normalize(text);
        const chosen = new Set(this.etiquetas.map(et => this.normalize(et.slug)));
        const available = this.allLabels.filter(l => !chosen.has(this.normalize(l.slug)));

        if (!query) {
            return available.map(l => ({ kind: 'existing', slug: l.slug, label: l }));
        }

        // Coincidencias: primero las que empiezan con lo escrito
        const matches = available
            .filter(l => this.normalize(l.slug).includes(query))
            .sort((a, b) => Number(!this.normalize(a.slug).startsWith(query)) - Number(!this.normalize(b.slug).startsWith(query)));

        // Parecidas por error de tipeo (p. ej. "mineira" vs "minería")
        const similar = this.allowCreate
            ? available.filter(l => !matches.includes(l) && this.isTypo(this.normalize(l.slug), query))
            : [];

        const options: LabelOption[] = [
            ...matches.map(l => ({ kind: 'existing' as const, slug: l.slug, label: l })),
            ...similar.map(l => ({ kind: 'similar' as const, slug: l.slug, label: l })),
        ];

        const exact = this.allLabels.find(l => this.normalize(l.slug) === query);
        if (exact && chosen.has(query)) {
            options.push({ kind: 'info', slug: `«${exact.slug}» ya está agregada` });
        } else if (!exact && chosen.has(query)) {
            options.push({ kind: 'info', slug: `«${this.clean(text)}» ya está agregada` });
        } else if (this.allowCreate && !exact) {
            options.push({ kind: 'create', slug: this.clean(text), similar: [...similar, ...matches] });
        }
        return options;
    }

    /** Para comparar: minúsculas, sin tildes ni signos, espacios simples */
    private normalize(value: string): string {
        return (value ?? '')
            .normalize('NFD').replace(/[̀-ͯ]/g, '')
            .toLowerCase()
            .replace(/[^\p{L}\p{N}\s]/gu, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    /** Texto a guardar para una etiqueta nueva: sin espacios de más */
    private clean(value: string): string {
        return (value ?? '').replace(/\s+/g, ' ').trim();
    }

    /** true si difieren en pocas letras (1 si son cortas, 2 si tienen 8 o más) */
    private isTypo(a: string, b: string): boolean {
        if (Math.min(a.length, b.length) < 4) {
            return false;
        }
        const max = Math.max(a.length, b.length) >= 8 ? 2 : 1;
        return Math.abs(a.length - b.length) <= max && this.distance(a, b) <= max;
    }

    /** Distancia de Levenshtein (cantidad de letras a cambiar, agregar o quitar) */
    private distance(a: string, b: string): number {
        let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
        for (let i = 1; i <= a.length; i++) {
            const current = [i];
            for (let j = 1; j <= b.length; j++) {
                current[j] = Math.min(
                    previous[j] + 1,
                    current[j - 1] + 1,
                    previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
                );
            }
            previous = current;
        }
        return previous[b.length];
    }

    private escape(value: string): string {
        return (value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }
}
